import { DatePicker, Menu, Tooltip } from '@affine/component';
import { startWeek } from '@affine/core/modules/day';
import { i18nTime, useI18n } from '@affine/i18n';
import { DocService, useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useContext } from 'react';

import { managerContext } from '../../common';
import * as styles from '../styles.css';
import type { PropertyRowValueProps } from '../types';

const MetaDateValueFactory = ({
  type,
}: {
  type: 'createDate' | 'updatedDate';
}) =>
  function ReadonlyDateValue() {
    const { docService } = useServices({
      DocService,
    });

    const docMeta = useLiveData(docService.doc.meta$);
    const value = docMeta?.[type];

    const date = value ? value : null;

    return (
      <Tooltip side="right" content={i18nTime(date)}>
        <div className={styles.propertyRowValueCell}>
          {i18nTime(date, {
            relative: {
              max: [1, 'day'],
              accuracy: 'minute',
            },
            absolute: {
              accuracy: 'day',
            },
          })}
        </div>
      </Tooltip>
    );
  };

export const DateValue = ({ property }: PropertyRowValueProps) => {
  const displayValue = property.value
    ? i18nTime(property.value, { absolute: { accuracy: 'day' } })
    : undefined;
  const manager = useContext(managerContext);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // show edit popup
  }, []);

  const handleChange = useCallback(
    (e: string) => {
      manager.updateCustomProperty(property.id, {
        value: e,
      });
    },
    [manager, property.id]
  );

  const t = useI18n();

  return (
    <Menu
      items={
        <DatePicker
          weekDays={t['com.affine.calendar-date-picker.week-days']()}
          monthNames={t['com.affine.calendar-date-picker.month-names']()}
          todayLabel={t['com.affine.calendar-date-picker.today']()}
          value={property.value}
          onChange={handleChange}
          startWeek={startWeek}
        />
      }
    >
      <div
        onClick={handleClick}
        className={styles.propertyRowValueCell}
        data-empty={!property.value}
      >
        {displayValue ??
          t['com.affine.doc-properties.property-value-placeholder']()}
      </div>
    </Menu>
  );
};

export const UpdatedDateValue = MetaDateValueFactory({
  type: 'updatedDate',
});

export const CreatedDateValue = MetaDateValueFactory({
  type: 'createDate',
});
